/**
 * WellnessCheckInCard — Daily 10-metric check-in for players.
 * Posts to /api/wellness/check-in. Idempotent per day.
 */
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, Moon, Zap, Droplet, Apple, Smile, AlertTriangle, Activity, Brain, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const METRICS = [
  { key: 'sleep_hours', label: 'Sleep (hours)', icon: Moon, min: 0, max: 12, step: 0.5, defaultVal: 8 },
  { key: 'sleep_quality', label: 'Sleep Quality', icon: Moon, min: 1, max: 10, step: 1, defaultVal: 7 },
  { key: 'recovery', label: 'Recovery', icon: Zap, min: 1, max: 10, step: 1, defaultVal: 7 },
  { key: 'hydration', label: 'Hydration', icon: Droplet, min: 1, max: 10, step: 1, defaultVal: 7 },
  { key: 'nutrition', label: 'Nutrition', icon: Apple, min: 1, max: 10, step: 1, defaultVal: 7 },
  { key: 'general_wellness', label: 'General Wellness', icon: Heart, min: 1, max: 10, step: 1, defaultVal: 7 },
  { key: 'pain_level', label: 'Pain Level', icon: AlertTriangle, min: 0, max: 10, step: 1, defaultVal: 0 },
  { key: 'training_load_rpe', label: 'RPE Today', icon: Activity, min: 0, max: 10, step: 1, defaultVal: 5 },
  { key: 'mood', label: 'Mood', icon: Smile, min: 1, max: 10, step: 1, defaultVal: 7 },
  { key: 'perceived_stress', label: 'Stress', icon: Brain, min: 1, max: 10, step: 1, defaultVal: 4 },
];

export default function WellnessCheckInCard() {
  const [today, setToday] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [values, setValues] = useState(() => Object.fromEntries(METRICS.map((m) => [m.key, m.defaultVal])));
  const [growthSpurt, setGrowthSpurt] = useState(false);
  const [pain_location, setPainLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/wellness/today')
      .then((r) => { if (mounted) setToday(r.data?.logged_today === false ? null : r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...values, growth_spurt_flag: growthSpurt };
      if (pain_location) payload.pain_location = pain_location;
      if (notes) payload.notes = notes;
      const res = await api.post('/wellness/check-in', payload);
      setToday(res.data);
      setExpanded(false);
      toast.success(`Check-in saved — ${res.data.traffic_light.toUpperCase()} (${res.data.composite_score}/10)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Card className="bg-zinc-900 border-zinc-800 p-5 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></Card>;
  }

  const lightColor = today?.traffic_light === 'green' ? 'bg-[#A3FF00]/15 text-[#A3FF00] border-[#A3FF00]/30' :
                     today?.traffic_light === 'amber' ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' :
                     today?.traffic_light === 'red' ? 'bg-red-500/15 text-red-300 border-red-500/30' :
                     'bg-zinc-800 text-zinc-400 border-zinc-700';

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="wellness-checkin-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-white flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#A3FF00]" />Daily Wellness Check-In
        </h3>
        {today && (
          <Badge className={lightColor} data-testid="wellness-traffic-light">
            {today.traffic_light?.toUpperCase()} · {today.composite_score}/10
          </Badge>
        )}
      </div>
      {today ? (
        <div className="flex items-center justify-between" data-testid="wellness-logged-today">
          <p className="text-zinc-400 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#A3FF00]" />Logged today. Your coach can see traffic-light status only.
          </p>
          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Cancel' : 'Update'}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-zinc-400 text-sm">Takes 60 seconds. Used to compute ACWR, surface injury risk, and update your PDS wellness pillar.</p>
          <Button size="sm" className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold" onClick={() => setExpanded(true)} data-testid="wellness-start-button">
            Log Check-In
          </Button>
        </div>
      )}

      {expanded && (
        <div className="mt-4 space-y-3" data-testid="wellness-form">
          <div className="grid grid-cols-2 gap-2">
            {METRICS.map((m) => (
              <div key={m.key} className="bg-zinc-950 border border-zinc-800 rounded-lg p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-zinc-400 text-[11px] flex items-center gap-1.5"><m.icon className="w-3.5 h-3.5" />{m.label}</span>
                  <span className="text-[#A3FF00] font-mono text-sm" data-testid={`wellness-value-${m.key}`}>{values[m.key]}</span>
                </div>
                <input
                  type="range"
                  min={m.min} max={m.max} step={m.step}
                  value={values[m.key]}
                  onChange={(e) => setValues({ ...values, [m.key]: m.step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value, 10) })}
                  className="w-full accent-[#A3FF00]"
                  data-testid={`wellness-slider-${m.key}`}
                />
              </div>
            ))}
          </div>
          {values.pain_level > 0 && (
            <input
              type="text"
              placeholder="Pain location (e.g. left hamstring)"
              value={pain_location}
              onChange={(e) => setPainLocation(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 text-white text-sm rounded-lg px-3 py-2"
              data-testid="wellness-pain-location"
            />
          )}
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={growthSpurt} onChange={(e) => setGrowthSpurt(e.target.checked)} className="accent-[#A3FF00]" />
            Growth spurt this week (PHV flag)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional, private)"
            rows={2}
            className="w-full bg-zinc-950 border border-zinc-800 text-white text-sm rounded-lg px-3 py-2"
            data-testid="wellness-notes"
          />
          <Button disabled={saving} onClick={submit} className="w-full bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold" data-testid="wellness-submit-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Save Check-In
          </Button>
        </div>
      )}
    </Card>
  );
}
